/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { ScrollArea, type BoxProps } from 'chakra-v3';
import React from 'react';

/**
 * Scrollbar Component
 *
 * A scrollbar component that matches the Figma design specifications:
 * - Track: 3px width, dark teal (text.darkTeal / colors.brand.tealDark) color, 4px border radius
 * - Thumb: 7px width, buttonCta (cta.solid / colors.brand.cta) color, 4px border radius
 *
 * @example
 * ```tsx
 * <Scrollbar height="400px">
 *   <YourContent />
 * </Scrollbar>
 * ```
 */
export interface ScrollbarProps extends BoxProps {
  children: React.ReactNode;
  /**
   * Height of the scrollable container
   */
  height?: string | number;
  /**
   * Maximum height of the scrollable container
   */
  maxHeight?: string | number;
  /**
   * Width of the scrollable container
   */
  width?: string | number;
  /**
   * Maximum width of the scrollable container
   */
  maxWidth?: string | number;
}

export const Scrollbar: React.FC<ScrollbarProps> = ({ children, ...rest }) => {
  return (
    <ScrollArea.Root variant="always" {...rest}>
      <ScrollArea.Viewport>
        <ScrollArea.Content pr="12px">{children}</ScrollArea.Content>
      </ScrollArea.Viewport>
      <ScrollArea.Scrollbar
        width="3px"
        css={{
          // Track styling - solid dark teal for better visibility
          backgroundColor: 'brand.tealDark',
          borderRadius: '4px',
          overflow: 'visible',
        }}
      >
        <ScrollArea.Thumb
          css={{
            backgroundColor: 'brand.cta',
            width: '7px',
            borderRadius: '4px',
            marginLeft: '-2px', // Center the 7px thumb in the 3px track (7-3)/2 = 2px offset
          }}
        />
      </ScrollArea.Scrollbar>
      <ScrollArea.Corner />
    </ScrollArea.Root>
  );
};
